import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Wifi, MapPin, Star, Coffee, Users, Heart, Trophy, TrendingUp } from "lucide-react";
import axios from "axios";
import { useBrand } from "../contexts/BrandContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WelcomeModal() {
  const brand = useBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [stats, setStats] = useState({ venues: 0, cities: 0, reviews: 0, remote_workers: 0 });

  // Storage key for tracking if user has seen welcome modal THIS SESSION
  // Using sessionStorage so it only appears once per visit/session
  const storageKey = `${brand.id}_welcome_seen_session`;

  useEffect(() => {
    setMounted(true);
    // Check if user has seen the welcome modal THIS SESSION
    const hasSeenWelcome = sessionStorage.getItem(storageKey);
    if (!hasSeenWelcome) {
      // Fetch real stats and show modal
      axios.get(`${API}/public/stats`)
        .then(res => setStats(res.data))
        .catch(() => {/* Use defaults */});
      
      // Small delay for better UX
      const timer = setTimeout(() => setIsOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [storageKey]);

  const handleClose = () => {
    // Mark as seen for this session only
    sessionStorage.setItem(storageKey, "true");
    setIsOpen(false);
  };

  // Brand-specific styles
  const isStrider = brand.id === 'strider';
  const gradientClass = isStrider 
    ? 'bg-gradient-to-br from-strider-teal to-teal-700' 
    : 'bg-gradient-to-br from-blue-600 to-blue-800';
  const subtitleColor = isStrider ? 'text-teal-100' : 'text-blue-100';
  const iconBg = isStrider ? 'bg-strider-teal/10' : 'bg-blue-600/10';
  const iconText = isStrider ? 'text-strider-teal' : 'text-blue-600';
  const buttonBg = isStrider ? 'bg-strider-teal hover:bg-teal-700' : 'bg-blue-600 hover:bg-blue-700';
  const headingColor = isStrider ? 'text-strider-charcoal' : 'text-gray-900';
  const freeBadgeBg = isStrider ? 'bg-teal-50 border-teal-200' : 'bg-green-50 border-green-200';
  const freeBadgeText = isStrider ? 'text-teal-700' : 'text-green-700';
  const freeBadgeSubtext = isStrider ? 'text-teal-600' : 'text-green-600';

  const features = [
    { icon: MapPin, title: "Find Workspaces", desc: "Discover cafes, pubs, hotels & coworking spaces near you" },
    { icon: Wifi, title: isStrider ? "Verified WiFi Speeds" : "WiFi & Power Info", desc: isStrider ? "Know the actual WiFi speed and power availability before you go" : "Know the WiFi speed and power outlet availability before you go" },
    { icon: Star, title: "Community Ratings", desc: "See ratings for noise level, atmosphere & laptop-friendliness" },
    { icon: Heart, title: "Save Favorites", desc: "Build your personal list of go-to work spots" },
  ];

  // Use portal to render outside parent container
  if (!mounted) return null;
  
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={handleClose}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden my-auto scale-[0.9] sm:scale-100 origin-center cursor-pointer"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
              data-testid="welcome-close-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header with Logo */}
            <div className={`${gradientClass} px-4 py-3 sm:px-6 sm:py-4 text-center text-white`}>
              <div className="inline-block p-1 sm:p-1.5 bg-white rounded-xl shadow-lg mb-2 sm:mb-2">
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="h-10 sm:h-12"
                  style={{ background: 'transparent' }}
                />
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-bold mb-1">{brand.welcomeTitle}</h2>
              <p className={`${subtitleColor} text-xs sm:text-sm`}>
                {brand.welcomeSubtitle}
              </p>
            </div>

            {/* Content */}
            <div className="px-4 py-4 sm:px-6 sm:py-6">
              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                {features.map((feature) => (
                  <div key={feature.title} className="flex items-start gap-2 sm:gap-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                      <feature.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconText}`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${headingColor} text-xs sm:text-sm`}>{feature.title}</h3>
                      <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Free Badge */}
              <div className={`${freeBadgeBg} border rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-center`}>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Coffee className={`w-4 h-4 sm:w-5 sm:h-5 ${freeBadgeSubtext}`} />
                  <span className={`font-bold ${freeBadgeText} text-sm sm:text-base`}>100% Free to Use</span>
                </div>
                <p className={`text-xs sm:text-sm ${freeBadgeSubtext}`}>
                  Browse venues, save favorites, and read reviews — all completely free, forever.
                </p>
              </div>

              {/* Community & Leaderboard CTA */}
              <Link
                to="/community"
                onClick={handleClose}
                className={`block w-full mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border-2 border-dashed ${isStrider ? 'border-teal-200 bg-teal-50/50 hover:bg-teal-50' : 'border-amber-200 bg-amber-50/50 hover:bg-amber-50'} transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${isStrider ? 'bg-teal-100' : 'bg-amber-100'} flex items-center justify-center`}>
                    <Trophy className={`w-5 h-5 ${isStrider ? 'text-teal-600' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold ${headingColor} text-sm`}>Join {(stats.remote_workers || 0).toLocaleString()}+ Remote Workers</h3>
                      <TrendingUp className={`w-3 h-3 ${isStrider ? 'text-teal-500' : 'text-green-500'}`} />
                    </div>
                    <p className="text-xs text-gray-500">Earn points, climb the leaderboard & win prizes!</p>
                  </div>
                </div>
              </Link>

              {/* CTA Button */}
              <button
                onClick={handleClose}
                className={`w-full ${buttonBg} text-white font-semibold py-3 sm:py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm sm:text-base`}
                data-testid="welcome-get-started-btn"
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                Start Exploring
              </button>

              {/* Stats - Real data from API */}
              <div className="flex items-center justify-center gap-4 sm:gap-6 mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-100">
                <div className="text-center">
                  <p className={`text-base sm:text-lg font-bold ${headingColor}`}>{stats.venues || 0}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Workspaces</p>
                </div>
                <div className="w-px h-6 sm:h-8 bg-gray-200" />
                <div className="text-center">
                  <p className={`text-base sm:text-lg font-bold ${headingColor}`}>{stats.cities || 0}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">UK Cities</p>
                </div>
                <div className="w-px h-6 sm:h-8 bg-gray-200" />
                <div className="text-center">
                  <p className={`text-base sm:text-lg font-bold ${headingColor}`}>{stats.reviews || 0}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Reviews</p>
                </div>
              </div>
              
              {/* Click anywhere hint */}
              <p className="text-center text-xs text-gray-400 mt-4 animate-pulse">
                Click anywhere to continue
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
